import * as Sentry from "@sentry/vue";
import type { App as VueApp } from "vue";
import type { Router } from "vue-router";

import env from "@/environmentVariables";

const PLACEHOLDER_PATTERN = /^\$\{[A-Z0-9_]+\}$/;
type SentryOptions = Parameters<typeof Sentry.init>[0];

export function optionalEnv(value: string | undefined): string | undefined {
  if (!value || PLACEHOLDER_PATTERN.test(value)) {
    return undefined;
  }
  return value;
}

export function parseSampleRate(
  value: string | undefined,
  key: string
): number | undefined {
  const normalized = optionalEnv(value);
  if (normalized === undefined) {
    return undefined;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    throw new Error(`${key} must be a number between 0 and 1, got ${value}.`);
  }
  return parsed;
}

// --- connection probing ---

function dsnToEnvelopeUrl(dsn: string): string {
  const url = new URL(dsn);
  const projectId = url.pathname.replace(/^\/+/, "");
  return `${url.protocol}//${url.host}/api/${projectId}/envelope/`;
}

export function buildTunnelUrl(apiBaseUrl: string): string {
  try {
    return `${new URL(apiBaseUrl).origin}/api/v1/sentry/tunnel`;
  } catch {
    return "/api/v1/sentry/tunnel";
  }
}

async function probeConnection(label: string, url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: "POST", mode: "cors", body: "" });
    if (!response.ok && response.status !== 400) {
      throw new Error(`HTTP ${response.status}`);
    }
    console.info(`sentry connection check: ${label} - OK`);
    return true;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(`sentry connection check: ${label} - ERROR: ${msg}`);
    return false;
  }
}

// --- module-level state for re-initialization ---

let _app: VueApp | undefined;
let _router: Router | undefined;
// null = never initialized; undefined = initialized without tunnel; string = initialized with this tunnel
let _currentTunnel: string | undefined | null = null;

const ANONYMOUS_REJECTION_PATTERN = /^Non-Error promise rejection captured with value: (undefined|null)$/;

const OVERLAY_SELECTOR = "ion-modal, ion-alert, ion-popover, ion-action-sheet, ion-loading, ion-toast";

type RouteInfo = { name?: unknown; path: string; params?: Record<string, unknown> };

/** What the page looked like at the moment of the rejection, injectable for tests. */
export type PageSnapshot = {
  overlays: string[];
  visibility_state: string;
  online: boolean;
  page_age_ms: number;
};

/** Inline overlays stay mounted while closed, Ionic marks every overlay that is not presented with `overlay-hidden`. */
export function takePageSnapshot(): PageSnapshot {
  return {
    overlays: Array.from(document.querySelectorAll(OVERLAY_SELECTOR))
      .filter((el) => !el.classList.contains("overlay-hidden"))
      .map((el) => el.tagName.toLowerCase()),
    visibility_state: document.visibilityState,
    online: navigator.onLine,
    page_age_ms: Math.round(performance.now()),
  };
}

/**
 * Promises rejected with `undefined` carry no stack trace, so the event is otherwise ungroupable noise.
 * Tag them with the route that was open and attach the page state, so a recurrence points at a view and
 * its open overlays; the fingerprint keeps recurrences on different views apart.
 * The route `path` has no query string, so no token in a query can end up in the event.
 */
export function tagAnonymousRejection<E extends Sentry.ErrorEvent>(
  event: E,
  route: RouteInfo,
  snapshot: () => PageSnapshot = takePageSnapshot
): E {
  const value = event.exception?.values?.[0]?.value ?? "";
  if (!ANONYMOUS_REJECTION_PATTERN.test(value)) {
    return event;
  }
  const routeName = String(route.name ?? "unnamed");
  event.tags = {
    ...event.tags,
    anonymous_rejection: "true",
    route_name: routeName,
    route_path: route.path,
  };
  event.contexts = {
    ...event.contexts,
    anonymous_rejection: {
      route: { name: routeName, path: route.path, params: route.params ?? {} },
      ...snapshot(),
    },
  };
  event.fingerprint = ["anonymous-rejection", routeName];
  return event;
}

/**
 * Leaves a breadcrumb for rejections with an empty reason, so the page state at that moment is part of the event.
 * Never prevents the default, Sentry's own handler still reports the rejection.
 */
export function logAnonymousRejection(
  reason: unknown,
  route: RouteInfo,
  snapshot: () => PageSnapshot = takePageSnapshot
): void {
  if (reason !== undefined && reason !== null) {
    return;
  }
  const { overlays } = snapshot();
  console.warn(`promise rejected with ${String(reason)} on ${route.path}, open overlays: [${overlays.join(", ")}]`);
}

const UNCOLLAPSED_CATEGORIES = /^(ui\.|navigation$)/;

function breadcrumbKey(crumb: Sentry.Breadcrumb): string {
  const { method, url } = (crumb.data ?? {}) as { method?: unknown; url?: unknown };
  if (typeof url === "string") {
    return `${crumb.category}|${String(method)}|${url.split("?")[0]}`;
  }
  return `${crumb.category}|${crumb.message}`;
}

/**
 * Collapses bursts of the same breadcrumb into one, so a flood (e.g. icon requests) does not push the clicks and
 * navigations before it out of the list. A burst is `minRun` or more breadcrumbs with the same key whose gaps are at most
 * `gapSeconds`; it is kept at the time of its last occurrence, with the count and time span in the message and data.
 * Clicks and navigations are never collapsed. Does not modify the given array.
 */
export function collapseBreadcrumbs(
  breadcrumbs: Sentry.Breadcrumb[],
  gapSeconds = 2,
  minRun = 3
): Sentry.Breadcrumb[] {
  const bursts = new Map<string, Sentry.Breadcrumb[][]>();
  for (const crumb of breadcrumbs) {
    if (UNCOLLAPSED_CATEGORIES.test(crumb.category ?? "")) continue;
    const key = breadcrumbKey(crumb);
    const runs = bursts.get(key) ?? [];
    const run = runs[runs.length - 1];
    const last = run?.[run.length - 1];
    if (run && last && (crumb.timestamp ?? 0) - (last.timestamp ?? 0) <= gapSeconds) {
      run.push(crumb);
    } else {
      runs.push([crumb]);
    }
    bursts.set(key, runs);
  }

  const replacement = new Map<Sentry.Breadcrumb, Sentry.Breadcrumb | null>();
  for (const runs of bursts.values()) {
    for (const run of runs) {
      if (run.length < minRun) continue;
      const first = run[0];
      const last = run[run.length - 1];
      const seconds = ((last.timestamp ?? 0) - (first.timestamp ?? 0)).toFixed(1);
      for (const crumb of run) replacement.set(crumb, null);
      replacement.set(last, {
        ...last,
        message: `${last.message ?? breadcrumbKey(last)} (x${run.length} within ${seconds} s)`,
        data: { ...last.data, count: run.length, first_timestamp: first.timestamp, last_timestamp: last.timestamp },
      });
    }
  }
  return breadcrumbs.flatMap((crumb) => {
    const collapsed = replacement.get(crumb);
    return collapsed === undefined ? [crumb] : collapsed === null ? [] : [collapsed];
  });
}

function buildSentryOptions(
  app: VueApp,
  router: Router,
  dsn: string,
  tunnel?: string
): SentryOptions {
  return {
    app,
    dsn,
    ...(tunnel !== undefined ? { tunnel } : {}),
    environment: optionalEnv(env.VITE_SENTRY_ENVIRONMENT),
    release: optionalEnv(env.VITE_SENTRY_RELEASE) ?? __GIT_COMMIT_FULL__,
    dist: __BUILD_TIME__,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.vueIntegration({ app, tracingOptions: { trackComponents: true } }),
    ],
    sendDefaultPii: false,
    maxBreadcrumbs: 300,
    beforeSend: (event) => {
      if (event.breadcrumbs) event.breadcrumbs = collapseBreadcrumbs(event.breadcrumbs);
      return tagAnonymousRejection(event, router.currentRoute.value);
    },
    initialScope: {
      tags: {
        git_commit: __GIT_COMMIT_FULL__,
        git_branch: __GIT_BRANCH__,
        build_time: __BUILD_TIME__,
      },
    },
    tracesSampleRate:
      parseSampleRate(
        env.VITE_SENTRY_TRACES_SAMPLE_RATE,
        "VITE_SENTRY_TRACES_SAMPLE_RATE"
      ) ?? 0,
  };
}

function configureSentry(dsn: string, tunnel?: string): void {
  if (!_app || !_router) return;
  if (_currentTunnel !== null && tunnel === _currentTunnel) return;
  _currentTunnel = tunnel;
  Sentry.init(buildSentryOptions(_app, _router, dsn, tunnel));
}

async function _probeAndConfigureTunnel(apiBaseUrl: string): Promise<void> {
  const dsn = optionalEnv(env.VITE_SENTRY_DSN);
  if (!dsn) return;

  const envelopeUrl = dsnToEnvelopeUrl(dsn);
  const dsnLabel = new URL(dsn).host;

  const directOk = await probeConnection(dsnLabel, envelopeUrl);
  if (directOk) return;

  const tUrl = buildTunnelUrl(apiBaseUrl);
  const apiLabel = (() => {
    try {
      return new URL(apiBaseUrl).host;
    } catch {
      return apiBaseUrl;
    }
  })();
  const tunnelOk = await probeConnection(apiLabel, tUrl);
  if (tunnelOk) {
    configureSentry(dsn, tUrl);
  }
}

export function initSentry(
  app: VueApp,
  router: Router,
  apiBaseUrl: string
): void {
  _app = app;
  _router = router;
  const dsn = optionalEnv(env.VITE_SENTRY_DSN);
  if (!dsn) return;
  // Registered before `Sentry.init`, so the breadcrumb precedes the event.
  window.addEventListener("unhandledrejection", (event) =>
    logAnonymousRejection(event.reason, router.currentRoute.value)
  );
  configureSentry(dsn);
  // Fire-and-forget: probing can switch transport, but initial error capture is active immediately.
  _probeAndConfigureTunnel(apiBaseUrl).catch((e) =>
    console.error("sentry init failed:", e)
  );
}

export function reconfigureSentry(apiBaseUrl: string): void {
  _probeAndConfigureTunnel(apiBaseUrl).catch((e) =>
    console.error("sentry reconfigure failed:", e)
  );
}

export function sentryPiniaPlugin() {
  return Sentry.createSentryPiniaPlugin({
    stateTransformer: (state: Record<string, unknown>) => {
      const { accessToken, imagesToken, objects, schemas, fetchedIcons, ...safe } = state;
      return {
        ...safe,
        objectsCount: Array.isArray(objects) ? objects.length : undefined,
      };
    },
  });
}
